import type { ReactNode } from "react";
import {
  LOCALES,
  LOCALE_LABELS,
  LOCALE_TAGS,
  localePath,
  storeLocale,
  useLocale,
  useMessages,
} from "../i18n";
import { ARTICLES_PATH, FAQ_PATH, SITE_NAME } from "../shared";
import { Logo } from "./Logo";
import type { Crumb } from "../seo";

/**
 * The frame around every page a reader arrives on from a search result.
 *
 * The reading pages are the only part of SprinkleDays that a stranger sees
 * before they have chosen anything, so this shell carries no app state at all:
 * no settings, no progress, no stored languages. It renders identically at
 * build time and in the browser, which is what lets the same components be
 * prerendered into static HTML.
 *
 * Every link is a plain anchor for the same reason, and the language switcher
 * most of all: a crawler has to be able to walk from one language to another,
 * and a control that only works once JavaScript has run is a control it cannot
 * follow. `hreflang` on each link says where it goes before it is followed.
 */

export function Breadcrumbs({ crumbs }: { crumbs: readonly Crumb[] }) {
  const t = useMessages();

  return (
    <nav className="crumbs" aria-label={t.nav.breadcrumb}>
      <ol>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          const path = new URL(crumb.url).pathname;

          return (
            <li key={crumb.url}>
              {isLast ? (
                <span aria-current="page">{crumb.name}</span>
              ) : (
                <a href={path}>{crumb.name}</a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The same page in the other two languages.
 *
 * `path` is the English route this page corresponds to, because every URL on
 * the site is derived from the English one. A page with no translation passes
 * nothing and the switcher stays away rather than offering a link that would
 * land the reader somewhere else entirely.
 */
export function LanguageSwitch({ path }: { path?: string }) {
  const locale = useLocale();
  const t = useMessages();

  if (!path) return null;

  return (
    <nav className="langswitch" aria-label={t.nav.language}>
      {LOCALES.map((option) => {
        const isCurrent = option === locale;

        return (
          <a
            key={option}
            className={`langswitch__link${isCurrent ? " is-current" : ""}`}
            href={localePath(option, path)}
            hrefLang={LOCALE_TAGS[option]}
            lang={LOCALE_TAGS[option]}
            aria-current={isCurrent ? "true" : undefined}
            aria-label={t.nav.languageOf(LOCALE_LABELS[option])}
            // A choice made by hand outranks the device from here on. The
            // anchor still navigates; this only records that it was chosen.
            onClick={() => storeLocale(option)}
          >
            {LOCALE_LABELS[option]}
          </a>
        );
      })}
    </nav>
  );
}

export function ReadingShell({
  children,
  path,
}: {
  children: ReactNode;
  /** The English route this page is, so the switcher can find its siblings. */
  path?: string;
}) {
  const locale = useLocale();
  const t = useMessages();
  const to = (target: string) => localePath(locale, target);

  return (
    <div className="reading">
      <header className="reading__header">
        <a href={to("/")} aria-label={`${SITE_NAME} home`}>
          <Logo />
        </a>
        <nav className="reading__nav" aria-label={t.nav.reading}>
          <a href={to(ARTICLES_PATH)}>{t.nav.guides}</a>
          <a href={to(FAQ_PATH)}>{t.nav.questions}</a>
          <LanguageSwitch path={path} />
          <a className="btn btn--ghost btn--md" href={to("/")}>
            <span>{t.nav.startCourse}</span>
          </a>
        </nav>
      </header>

      {children}

      <footer className="reading__footer">
        <Logo />
        <p>{t.nav.footerBlurb}</p>
        <nav className="reading__footer-nav" aria-label={t.nav.footer}>
          <a href={to("/")}>{t.nav.startCourse}</a>
          <a href={to(ARTICLES_PATH)}>{t.nav.allGuides}</a>
          <a href={to(FAQ_PATH)}>{t.nav.questions}</a>
        </nav>
      </footer>
    </div>
  );
}
